import { AUTH0_CALLBACK_PATH, SIGNED_OUT_PATH } from '../../utils/auth0Config';

/**
 * Carries the page a sign-in started from through the Auth0 round trip. The shell's redirect
 * URI is always the callback page, so the page travels in `appState` instead.
 */

/**
 * Pages a sign-in can start from that are not worth returning to: the callback page (a retry
 * after a failed code exchange) and the signed-out landing page. A sign-in from either lands home.
 */
const NO_RETURN_PATHS: ReadonlySet<string> = new Set([AUTH0_CALLBACK_PATH, SIGNED_OUT_PATH]);

/**
 * Drops the parameters of an OAuth callback, matched the way `@auth0/auth0-react` recognises one
 * (`code`, `connect_code` or `error`, together with `state`). A module that runs its own Auth0
 * client consumes any URL carrying them as its sign-in callback, so restoring one after the
 * shell's sign-in would replay a stale callback, which fails. Without them the module just signs
 * in again. A lone `state` or `code` is an app's own parameter and is kept.
 */
function withoutAuthCallbackParams(search: string): string {
  const params = new URLSearchParams(search);
  const isCallback =
    !!(params.get('code') || params.get('connect_code') || params.get('error')) &&
    !!params.get('state');
  if (!isCallback) return search;
  ['code', 'connect_code', 'error', 'error_description', 'state'].forEach((name) =>
    params.delete(name)
  );
  const rest = params.toString();
  return rest ? `?${rest}` : '';
}

/** The page to come back to after signing in: the current path, query and hash. */
export function signInReturnPath(): string | undefined {
  const { pathname, search, hash } = window.location;
  if (NO_RETURN_PATHS.has(pathname)) return undefined;
  return `${pathname}${withoutAuthCallbackParams(search)}${hash}`;
}

/**
 * The `appState` a sign-in carries through Auth0, read back by `restoreSignInReturnPath`. Both
 * ends live here because Auth0 types `appState` loosely (any key is accepted), so a key renamed on
 * one side only would still typecheck.
 */
export function signInAppState(): { returnTo: string | undefined } {
  return { returnTo: signInReturnPath() };
}

/**
 * A path that resolves on this origin. Judged by resolving it the way `replaceState` will, since
 * the URL parser drops tabs and newlines and treats `\` as `/` — so `//host/x`, `/\host/x` and
 * `/\n/host/x` all point elsewhere. `replaceState` throws on those, and a throw fails the sign-in.
 */
function isSameOriginPath(value: unknown): value is string {
  if (typeof value !== 'string' || !value.startsWith('/')) return false;
  try {
    return new URL(value, window.location.origin).origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Replaces the callback URL (and its `?code&state`) with the page the sign-in started from, or
 * the home page when it saved none.
 *
 * `BrowserRouter` mounted while the URL was still the callback page and only follows `popstate`,
 * so it is told about the change. Without that it stays on the callback page, which no route
 * matches, and the catch-all sends the user home whatever the URL now says.
 */
export function restoreSignInReturnPath(appState: { returnTo?: unknown } | undefined): void {
  const returnTo = appState?.returnTo;
  const target = isSameOriginPath(returnTo) ? returnTo : '/';
  // Keeps the router's own history entry state (its index), which a bare `{}` would drop.
  window.history.replaceState(window.history.state, document.title, target);
  window.dispatchEvent(new PopStateEvent('popstate'));
}
